import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { getTranslation } from '../translations/translations';

// Constants for decentralized configuration
export const CONFIG = {
  EUR_TO_RON: 5,
  FREE_SHIPPING_THRESHOLD_KG: 10,
  SHIPPING_COST_EUR: 8
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'ro';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  useEffect(() => {
    document.documentElement.lang = language === 'ro' ? 'ro' : 'en';
  }, [language]);

  const setLanguageSafe = (lang) => {
    setLanguage(lang);
  };

  const currency = language === 'ro' ? 'RON' : 'EUR';
  const currencySymbol = language === 'ro' ? 'lei' : '€';

  const formatPrice = useCallback((priceInEur) => {
    if (language === 'ro') {
      const priceInRon = priceInEur * CONFIG.EUR_TO_RON;
      return `${priceInRon.toFixed(2)} lei`;
    }
    return `€${priceInEur.toFixed(2)}`;
  }, [language]);

  const t = useCallback(
    (key) => getTranslation(language, key),
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage: setLanguageSafe,
      currency,
      currencySymbol,
      formatPrice,
      EUR_TO_RON: CONFIG.EUR_TO_RON,
      t
    }),
    [language, currency, currencySymbol, formatPrice, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
